/**
 * CaseHeader — Displays case/claim identity with role-appropriate labeling.
 *
 * Design: Black + Orange only. Uses CSS variables for all colors.
 * Type stack: DM Serif (headings), Outfit (body), DM Mono (IDs/numbers).
 */

import React from 'react';
import { BRAND_NAME } from '@/config/brand';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { CaseTypeChip } from '@/components/shared/CaseTypeChip';
import { PillarScopeChip } from '@/components/shared/PillarScopeChip';
import type { CaseProfilePayload } from '@/components/shared/caseProfile';

export type WorkspaceRole = 'attorney' | 'accountant' | 'adjuster' | 'data_specialist' | 'default';

interface CaseHeaderProps {
  /** Unified case ID (normalized from claimId/caseId/reportId) */
  caseId: string;
  /** Insured name */
  insuredName?: string;
  /** Loss date */
  lossDate?: string;
  /** Insurance carrier */
  carrier?: string;
  /** Current stage */
  currentStage?: string;
  /** Claim value (attorney: planned demand) */
  claimValue?: number;
  /**
   * DRAFT-lifecycle status (attorney). "final" => externally attested (Signal C) =>
   * the header chip flips DRAFT → ATTESTED. Any other value (or undefined for other
   * roles) => DRAFT / no chip. See attorney.py CaseDetailResponse.status.
   */
  status?: string;
  /** Elected sworn BI method (attorney): bottom_up / top_down / reconciled. */
  method?: string;
  /** True while the elected method is still loading (or its load failed): render a muted — instead of claiming "Not Elected". */
  methodPending?: boolean;
  /** Loss/peril event type (attorney), e.g. "Hurricane". */
  eventType?: string;
  /** Role lens for labeling */
  role?: WorkspaceRole;
  /** Case profile (attorney) — resolved posture SoT, read-only (CT-0 0.7). */
  caseProfile?: CaseProfilePayload | null;
  /** Optional className */
  className?: string;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    let date: Date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = new Date(dateStr);
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

/** Title-case a raw token ("fire" → "Fire", "civil_authority" → "Civil Authority"). */
function titleCase(raw?: string): string {
  if (!raw) return '';
  return raw.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Elected sworn BI method → display label; undefined => "not elected". */
function formatMethod(method?: string): string | undefined {
  if (!method) return undefined;
  const m = method.toLowerCase();
  if (m === 'bottom_up') return 'Bottom-Up';
  if (m === 'top_down') return 'Top-Down';
  if (m === 'reconciled') return 'Reconciled';
  if (m === 'standard_bi') return undefined; // default, not an elected method
  return method.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function CaseHeader({
  caseId,
  insuredName,
  lossDate,
  carrier,
  currentStage,
  claimValue,
  status,
  method,
  methodPending = false,
  eventType,
  role = 'default',
  className = '',
  caseProfile,
}: CaseHeaderProps) {
  const roleLabel = role === 'attorney' ? 'Case' : role === 'accountant' ? 'Report' : 'Claim';

  // DRAFT-lifecycle chip is attorney-only (rendered iff status is provided). Amber
  // (--color-draft) matches the shipped PDF/DOCX watermark so screen + documents read
  // as one DRAFT language, and stays SEPARATE from the orange accent per design law.
  const showLifecycleChip = role === 'attorney' && !!status;
  // CT-0 0.7 — read-only posture chip, attorney-only, rendered iff the resolved
  // case_profile payload arrived on the case fetch.
  const showCaseTypeChip = role === 'attorney' && caseProfile != null;
  const isAttested = status === 'final';
  const methodLabel = formatMethod(method);

  return (
    <header
      className={`bg-[var(--color-bg-surface)] border-b border-[var(--color-border)] px-6 py-4 ${className}`}
      data-testid="case-header"
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Identity */}
        <div className="flex-1 min-w-0">
          {/* Breadcrumb / context */}
          <div className="flex items-center gap-2 text-[var(--color-text-muted)] text-xs font-medium mb-1">
            <span>{BRAND_NAME}</span>
            <span>/</span>
            <span>{roleLabel}</span>
          </div>

          {/* Main title: Insured name */}
          <h1 className="font-serif text-xl text-[var(--color-text-primary)] truncate">
            {insuredName || 'Unnamed Claim'}
          </h1>

          {/* Sub-line: ID · EVENT · METHOD · Carrier. For the attorney the EVENT and the
              elected METHOD carry the K3 header context; other roles keep Loss/Carrier. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-[var(--color-text-secondary)]">
            <span className="font-mono text-[var(--color-text-muted)]">
              {caseId.slice(0, 8).toUpperCase()}
            </span>
            {role === 'attorney' ? (
              <>
                {(eventType || lossDate) && (
                  <span className="flex items-center gap-1">
                    <span className="text-[var(--color-text-dim)]">Event:</span>
                    {eventType ? titleCase(eventType) : '—'}
                    {lossDate && <span className="text-[var(--color-text-muted)]">· {formatDate(lossDate)}</span>}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <span className="text-[var(--color-text-dim)]">Method:</span>
                  {methodLabel ? (
                    <span className="font-mono uppercase tracking-wide text-[var(--color-text-secondary)]">{methodLabel}</span>
                  ) : methodPending ? (
                    <span className="font-mono text-[var(--color-text-muted)]" data-testid="method-pending">—</span>
                  ) : (
                    <span className="font-mono uppercase tracking-wide text-[var(--color-text-muted)]">— Not Elected</span>
                  )}
                </span>
              </>
            ) : (
              lossDate && (
                <span className="flex items-center gap-1">
                  <span className="text-[var(--color-text-dim)]">Loss:</span>
                  {formatDate(lossDate)}
                </span>
              )
            )}
            {carrier && (
              <span className="flex items-center gap-1">
                <span className="text-[var(--color-text-dim)]">Carrier:</span>
                {carrier}
              </span>
            )}
          </div>
        </div>

        {/* Right: DRAFT-lifecycle chip (attorney) + Stage + Demand */}
        <div className="flex flex-col items-end gap-2">
          {showLifecycleChip &&
            (isAttested ? (
              // ATTESTED — externally attested (Signal C): monochrome outline, no hue.
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[3px] border border-[var(--color-verified)] text-[10px] font-mono uppercase tracking-[0.08em] text-[var(--color-text-secondary)]"
                title="Externally attested by the CPA."
                data-testid="lifecycle-chip"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-verified)]" />
                Attested
              </span>
            ) : (
              // DRAFT — amber, matches the document watermark; separate from the accent.
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[3px] border border-[var(--color-draft)] bg-[var(--color-draft-tint)] text-[10px] font-mono uppercase tracking-[0.08em] text-[var(--color-draft)]"
                title="All deliverables are drafts until external CPA attestation."
                data-testid="lifecycle-chip"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-draft)]" />
                Draft — Pending CPA Attestation
              </span>
            ))}
          {showCaseTypeChip && <CaseTypeChip profile={caseProfile} />}
          {/* CT-P P.3 — pillar scope chip (attorney); self-hides on default ["bi"] scope. */}
          {role === 'attorney' && caseProfile != null && <PillarScopeChip pillars={caseProfile.pillars} />}
          {currentStage && (
            <span className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">
              {currentStage}
            </span>
          )}
          {/* Label the figure so it can't be mistaken for the Pro Forma total on the
              Deliverables stage — this is the planned DEMAND (K3 header spec). */}
          {typeof claimValue === 'number' && (
            <div className="flex flex-col items-end">
              {role === 'attorney' && (
                <span className="text-[10px] font-mono uppercase tracking-[0.08em] text-[var(--color-text-dim)]">
                  Demand
                </span>
              )}
              <FinancialValue value={claimValue} size="lg" />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default CaseHeader;
